import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LoadingIndicator from './LoadingIndicator';

function isDarkTheme() {
  try {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  } catch (_) {
    return false;
  }
}

const BandwidthChart = ({ data }) => {
  const convertDataToDynamicUnit = (rows) => {
    const maxValue = Math.max(
      ...rows.map((item) => item.uprate),
      ...rows.map((item) => item.downrate)
    );

    let conversionFactor = 1;
    let unitLabel = 'bps';

    if (maxValue >= 1000000000) {
      conversionFactor = 1 / 1000000000;
      unitLabel = 'Gbps';
    } else if (maxValue >= 1000000) {
      conversionFactor = 1 / 1000000;
      unitLabel = 'Mbps';
    } else if (maxValue >= 1000) {
      conversionFactor = 1 / 1000;
      unitLabel = 'kbps';
    }

    const convertedData = rows.map((item) => ({
      ...item,
      uprate: item.uprate * conversionFactor,
      downrate: item.downrate * conversionFactor,
      unitLabel,
    }));

    return { convertedData, unitLabel };
  };

  const safeData = Array.isArray(data) ? data : [];
  const { convertedData, unitLabel } = convertDataToDynamicUnit(
    safeData.length ? safeData : [{ uprate: 0, downrate: 0, timestamp: Date.now() }]
  );
  const dark = isDarkTheme();
  const axisColor = dark ? '#9ca3af' : '#6c757d';
  const gridColor = dark ? '#3a3f44' : '#dee2e6';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const formattedDate = new Date(payload[0].payload.timestamp).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      return (
        <div className="bandwidth-chart__tooltip">
          <p>{formattedDate}</p>
          <p style={{ color: '#8884d8' }}>{`Upload: ${payload[0].value.toFixed(2)} ${unitLabel}`}</p>
          <p style={{ color: '#82ca9d' }}>{`Download: ${payload[1].value.toFixed(2)} ${unitLabel}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bandwidth-chart">
      {(!data || data.length === 0) && (
        <LoadingIndicator className="text-center text-secondary" message="Loading bandwidth data ..." />
      )}
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={convertedData} margin={{ top: 12, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="timestamp"
            tick={{ fill: axisColor, fontSize: 11 }}
            stroke={gridColor}
            tickFormatter={(timestamp) =>
              new Date(timestamp).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            }
          />
          <YAxis tick={{ fill: axisColor, fontSize: 11 }} stroke={gridColor} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ color: axisColor, paddingTop: 8 }}
            formatter={(value) => <span style={{ color: axisColor }}>{value}</span>}
          />
          <Area
            type="monotone"
            dataKey="uprate"
            name={`Upload (${unitLabel})`}
            stroke="#8884d8"
            fillOpacity={1}
            fill="url(#colorUv)"
          />
          <Area
            type="monotone"
            dataKey="downrate"
            name={`Download (${unitLabel})`}
            stroke="#82ca9d"
            fillOpacity={1}
            fill="url(#colorPv)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BandwidthChart;
